import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import App from './App';
import './styles/globals.css';
import './landing/landing.css';

// ─────────────────────────────────────────────────────────────────────────────
// Sentry (error monitoring)
// ─────────────────────────────────────────────────────────────────────────────

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENV ?? 'development',
  enabled: import.meta.env.VITE_APP_ENV === 'production',
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
});

// ─────────────────────────────────────────────────────────────────────────────
// Datadog RUM (only in production to avoid noise in dev)
// ─────────────────────────────────────────────────────────────────────────────

if (import.meta.env.VITE_APP_ENV === 'production' && import.meta.env.VITE_DD_CLIENT_TOKEN) {
  import('@datadog/browser-rum').then(({ datadogRum }) => {
    datadogRum.init({
      applicationId:  import.meta.env.VITE_DD_APPLICATION_ID,
      clientToken:    import.meta.env.VITE_DD_CLIENT_TOKEN,
      site:           import.meta.env.VITE_DD_SITE ?? 'datadoghq.com',
      service:        import.meta.env.VITE_DD_SERVICE ?? 'callxcel-shopify',
      env:            import.meta.env.VITE_DD_ENV ?? 'production',
      sessionSampleRate:    100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
    datadogRum.startSessionReplayRecording();
  });

  import('@datadog/browser-logs').then(({ datadogLogs }) => {
    datadogLogs.init({
      clientToken:  import.meta.env.VITE_DD_CLIENT_TOKEN,
      site:         import.meta.env.VITE_DD_SITE ?? 'datadoghq.com',
      service:      import.meta.env.VITE_DD_SERVICE ?? 'callxcel-shopify',
      env:          import.meta.env.VITE_DD_ENV ?? 'production',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Hotjar (only in production)
// ─────────────────────────────────────────────────────────────────────────────

if (import.meta.env.VITE_APP_ENV === 'production' && import.meta.env.VITE_HOTJAR_ID) {
  import('react-hotjar').then(({ hotjar }) => {
    hotjar.initialize({
      id:      Number(import.meta.env.VITE_HOTJAR_ID),
      sv:      Number(import.meta.env.VITE_HOTJAR_SV ?? 6),
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mount
//
// react-query / next-themes / sonner are NOT initialized here — they're moved
// inside AppRoot (lazy-loaded at /app) so the marketing landing page doesn't
// download them on first paint.
// ─────────────────────────────────────────────────────────────────────────────

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

// ─────────────────────────────────────────────────────────────────────────────
// Error boundary fallback
// ─────────────────────────────────────────────────────────────────────────────

function ErrorFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
        color: '#3d4259',
      }}
    >
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d4392b" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#131628' }}>Something went wrong</h2>
      <p style={{ fontSize: 13, maxWidth: 360, textAlign: 'center' }}>
        An unexpected error occurred. Please refresh the page to continue.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '8px 16px',
          background: 'linear-gradient(180deg, #2d3a72, #1a2452)',
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          fontSize: 13.5,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Refresh page
      </button>
    </div>
  );
}
